/**
 * Renders a news article in the visual style of its source outlet, with the
 * endorser shown as an EMBEDDED TWEET (as in the original Study 1).
 *
 * Layout is held CONSTANT (single professional template) so layout is not a
 * confound — only outlet branding (logo, colors, nav, fonts) varies. The
 * endorser quote is rendered as a realistic X/Twitter (or Truth Social) embed
 * card inside the article, which is the Source-Endorser Credibility cue.
 *
 * Realism chrome (section kicker, byline row with outlet attribution + reading
 * time, share-affordance bar, outlet footer) is modeled on the original 2018
 * fnortnor.com saved outlet pages. Every realism element is IDENTICAL across all
 * articles (structure is constant; only branding varies), so it adds verisimilitude
 * without introducing a layout confound. The share bar shows affordances only —
 * NO share/like counts — so no uncontrolled social-proof cue is introduced.
 */

import React from 'react';
import { NewsArticle } from '../../data/newsArticlesWithCongruence';

interface Props {
  article: NewsArticle;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  // Per-region hover/dwell tracking. Called with a region label (e.g.
  // 'source-masthead', 'endorser', 'headline', 'body-p2') on enter/leave so the
  // analysis can measure dwell on each cue region (trust vs epistemic vs gist).
  onRegionHover?: (region: string, entering: boolean) => void;
}

type OutletStyle = {
  headerBg: string; headerText: string; logoText: string; logoFont: string;
  logoBold: boolean; navItems: string[]; accentColor: string;
  headlineFont: string; bodyFont: string; bylineColor: string;
  // Optional path to an official logo image (e.g. '/logos/cnn.svg'). When set,
  // the masthead renders this image instead of the CSS recreation. Leave unset
  // to use the built-in brand-faithful wordmark/mark.
  logoSrc?: string;
};

const SERIF = 'Georgia, Cambria, "Iowan Old Style", "Source Serif Pro", serif';
const SANS = '"Helvetica Neue", Arial, sans-serif';
// Clean modern stack for ARTICLE BODY text. Body font is cosmetic (not a measured
// cue), so it is unified across outlets here to read like a contemporary news
// site and avoid the Times-New-Roman/Computer-Modern ("LaTeX/old computer") feel
// the per-outlet serif fallback produced. Headlines keep their per-outlet font.
const BODY = '-apple-system, system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

// Topic → section label shown as the kicker above the headline, the way real
// outlet pages tag a story (Washington Times original used <article:section>).
// Uniform mapping across outlets so the kicker is structure, not a cue.
const TOPIC_SECTION: Record<string, string> = {
  climate: 'Climate', energy: 'Energy', economy: 'Economy', health: 'Health',
  guns: 'Politics', immigration: 'Politics', jan6: 'Politics',
  abortion: 'Politics', character: 'Politics', election: 'Politics',
};
const sectionFor = (a: NewsArticle): string => TOPIC_SECTION[a.topic] || 'Politics';

// Honest reading-time estimate from the body (≈225 wpm), the way modern article
// pages show "N min read". Derived, not fabricated.
const readingMinutes = (body: string): number =>
  Math.max(1, Math.round(body.trim().split(/\s+/).length / 225));

const sourceStyles: Record<string, OutletStyle> = {
  'CNN': { headerBg: '#fff', headerText: '#1a1a1a', logoText: 'CNN', logoFont: SANS, logoBold: true, navItems: ['World', 'U.S.', 'Politics', 'Business', 'Health', 'Science'], accentColor: '#cc0000', headlineFont: SERIF, bodyFont: SANS, bylineColor: '#5e5e5e', logoSrc: '/logos/cnn.svg' },
  'Associated Press': { headerBg: '#fff', headerText: '#333', logoText: 'AP NEWS', logoFont: SANS, logoBold: true, navItems: ['Top Stories', 'World', 'U.S.', 'Politics', 'Business', 'Science'], accentColor: '#e31c23', headlineFont: SERIF, bodyFont: SANS, bylineColor: '#666' },
  'Reuters': { headerBg: '#fff', headerText: '#333', logoText: 'Reuters', logoFont: SERIF, logoBold: false, navItems: ['World', 'Business', 'Markets', 'Legal', 'Technology'], accentColor: '#ff6600', headlineFont: SERIF, bodyFont: SANS, bylineColor: '#666', logoSrc: '/logos/reuters.svg' },
  'Vox': { headerBg: '#fff200', headerText: '#000', logoText: 'VOX', logoFont: SANS, logoBold: true, navItems: ['Politics', 'Policy', 'Culture', 'Science', 'Explainers'], accentColor: '#000', headlineFont: SANS, bodyFont: SERIF, bylineColor: '#666', logoSrc: '/logos/vox.svg' },
  'PBS NewsHour': { headerBg: '#2638c4', headerText: '#fff', logoText: 'PBS NewsHour', logoFont: SANS, logoBold: true, navItems: ['Politics', 'Nation', 'World', 'Economy', 'Science'], accentColor: '#2638c4', headlineFont: SERIF, bodyFont: SANS, bylineColor: '#666' },
  'The Wall Street Journal': { headerBg: '#fff', headerText: '#000', logoText: 'THE WALL STREET JOURNAL.', logoFont: SERIF, logoBold: false, navItems: ['World', 'U.S.', 'Politics', 'Economy', 'Business', 'Markets'], accentColor: '#0274b6', headlineFont: SERIF, bodyFont: SERIF, bylineColor: '#555', logoSrc: '/logos/wsj.svg' },
  'Fox News': { headerBg: '#fff', headerText: '#0c2340', logoText: 'FOX NEWS', logoFont: SANS, logoBold: true, navItems: ['U.S.', 'World', 'Opinion', 'Politics', 'Business'], accentColor: '#0c2340', headlineFont: SANS, bodyFont: SERIF, bylineColor: '#666', logoSrc: '/logos/fox-news.svg' },
  'Fox Business': { headerBg: '#0c2340', headerText: '#fff', logoText: 'FOX BUSINESS', logoFont: SANS, logoBold: true, navItems: ['Markets', 'Economy', 'Politics', 'Technology', 'Energy'], accentColor: '#c8a04a', headlineFont: SANS, bodyFont: SERIF, bylineColor: '#666' },
  'Breitbart': { headerBg: '#000', headerText: '#fff', logoText: 'BREITBART', logoFont: SANS, logoBold: true, navItems: ['Politics', 'Economy', 'Border', 'National Security', 'Tech'], accentColor: '#cc4b00', headlineFont: SANS, bodyFont: SANS, bylineColor: '#777', logoSrc: '/logos/breitbart.svg' },
  'The Daily Wire': { headerBg: '#0d1b2a', headerText: '#fff', logoText: 'DailyWire+', logoFont: SANS, logoBold: true, navItems: ['News', 'Politics', 'Economy', 'Culture', 'Opinion'], accentColor: '#c8102e', headlineFont: SANS, bodyFont: SANS, bylineColor: '#777' },
  'National Review': { headerBg: '#fff', headerText: '#1a1a1a', logoText: 'NATIONAL REVIEW', logoFont: SERIF, logoBold: false, navItems: ['News', 'Politics', 'Economy', 'Opinion', 'Magazine'], accentColor: '#003366', headlineFont: SERIF, bodyFont: SERIF, bylineColor: '#666' },
  'MSNBC': { headerBg: '#fff', headerText: '#222', logoText: 'msnbc', logoFont: SANS, logoBold: true, navItems: ['Politics', 'Opinion', 'U.S. News', 'World', 'Business'], accentColor: '#4f2d7f', headlineFont: SANS, bodyFont: SANS, bylineColor: '#666', logoSrc: '/logos/msnbc.svg' },
  'HuffPost': { headerBg: '#2e7061', headerText: '#fff', logoText: 'HuffPost', logoFont: SANS, logoBold: true, navItems: ['News', 'Politics', 'Environment', 'Entertainment', 'Life'], accentColor: '#0dbe98', headlineFont: SANS, bodyFont: SANS, bylineColor: '#666' },
  'Rolling Stone': { headerBg: '#000', headerText: '#fff', logoText: 'ROLLING STONE', logoFont: SERIF, logoBold: true, navItems: ['Politics', 'Culture', 'Music', 'TV & Movies', 'RS Recommends'], accentColor: '#cc0000', headlineFont: SERIF, bodyFont: SANS, bylineColor: '#777', logoSrc: '/logos/rolling-stone.svg' },
  'The Guardian': { headerBg: '#fff', headerText: '#052962', logoText: 'The Guardian', logoFont: SERIF, logoBold: true, navItems: ['US', 'World', 'Politics', 'Environment', 'Opinion'], accentColor: '#052962', headlineFont: SERIF, bodyFont: SANS, bylineColor: '#666', logoSrc: '/logos/guardian.svg' },
  'New York Post': { headerBg: '#c60800', headerText: '#fff', logoText: 'New York Post', logoFont: SERIF, logoBold: true, navItems: ['News', 'Metro', 'Politics', 'Opinion', 'Business'], accentColor: '#c60800', headlineFont: SANS, bodyFont: SANS, bylineColor: '#666', logoSrc: '/logos/nypost.svg' },
  'Washington Examiner': { headerBg: '#0b1f3a', headerText: '#fff', logoText: 'Washington Examiner', logoFont: SERIF, logoBold: true, navItems: ['News', 'Politics', 'Policy', 'Opinion', 'Restoring America'], accentColor: '#b01116', headlineFont: SERIF, bodyFont: SANS, bylineColor: '#666' },
  'The Washington Times': { headerBg: '#13294b', headerText: '#fff', logoText: 'The Washington Times', logoFont: SERIF, logoBold: false, navItems: ['News', 'Politics', 'National', 'World', 'Opinion'], accentColor: '#13294b', headlineFont: SERIF, bodyFont: SERIF, bylineColor: '#666', logoSrc: '/logos/washington-times.svg' },
  'The Gateway Pundit': { headerBg: '#1c2b3a', headerText: '#fff', logoText: 'The Gateway Pundit', logoFont: SANS, logoBold: true, navItems: ['Home', 'Politics', 'Elections', 'Opinion', 'Trending'], accentColor: '#b8860b', headlineFont: SANS, bodyFont: SANS, bylineColor: '#777' },
  'One America News': { headerBg: '#0a1f44', headerText: '#fff', logoText: 'OAN · One America News', logoFont: SANS, logoBold: true, navItems: ['Politics', 'U.S.', 'World', 'Business', 'Tech'], accentColor: '#c8102e', headlineFont: SANS, bodyFont: SANS, bylineColor: '#777' },
  'The Epoch Times': { headerBg: '#b8860b', headerText: '#fff', logoText: 'THE EPOCH TIMES', logoFont: SERIF, logoBold: true, navItems: ['China', 'US', 'Politics', 'Health', 'Opinion'], accentColor: '#b8860b', headlineFont: SERIF, bodyFont: SERIF, bylineColor: '#666' },
};

// ── Embedded tweet (Source-Endorser Credibility cue) ──────────────────────

const TweetEmbed: React.FC<{ endorser: NonNullable<NewsArticle['endorser']> }> = ({ endorser }) => {
  const isTruth = endorser.platform === 'Truth Social';
  const accent = isTruth ? '#5448c8' : '#1d9bf0';
  const initials = endorser.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const Icon = ({ d }: { d: string }) => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="#536471" aria-hidden><path d={d} /></svg>
  );
  return (
    <div style={{
      border: '1px solid #cfd9de', borderRadius: '14px', padding: '12px 16px',
      margin: '4px 0 20px', maxWidth: '550px', background: '#fff',
      fontFamily: '"Helvetica Neue", Arial, sans-serif',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{
          width: 44, height: 44, borderRadius: '50%', background: accent, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15,
        }}>{initials}</div>
        <div style={{ lineHeight: 1.2, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontWeight: 700, color: '#0f1419', fontSize: 15 }}>{endorser.name}</span>
            <svg viewBox="0 0 24 24" width="16" height="16" fill={accent} aria-hidden>
              <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.79-4-4-4-.495 0-.965.084-1.4.238C14.55 2.495 13.18 1.5 11.5 1.5S8.45 2.495 7.81 3.738C7.375 3.584 6.905 3.5 6.41 3.5c-2.21 0-4 1.79-4 4 0 .495.084.965.238 1.4C1.375 9.55.5 10.92.5 12.5s.875 2.95 2.148 3.6c-.154.435-.238.905-.238 1.4 0 2.21 1.79 4 4 4 .495 0 .965-.084 1.4-.238.64 1.243 2.01 2.238 3.69 2.238s3.05-.995 3.69-2.238c.435.154.905.238 1.4.238 2.21 0 4-1.79 4-4 0-.495-.084-.965-.238-1.4 1.273-.65 2.148-2.02 2.148-3.6zm-6.072-2.428l-4.5 6c-.13.173-.33.282-.546.296h-.05c-.2 0-.392-.08-.532-.22l-2.5-2.5c-.293-.293-.293-.767 0-1.06s.767-.293 1.06 0l1.894 1.893 3.97-5.293c.248-.33.718-.398 1.05-.15.33.248.398.718.15 1.05z" />
            </svg>
          </div>
          <span style={{ color: '#536471', fontSize: 14 }}>{endorser.handle}</span>
        </div>
        <span style={{ color: '#536471', fontWeight: 800, fontSize: 20 }}>{isTruth ? 'ᵀ' : '𝕏'}</span>
      </div>
      <div style={{ color: '#0f1419', fontSize: 17, lineHeight: 1.4, margin: '10px 0 8px' }}>
        {endorser.text}
      </div>
      <div style={{ color: '#536471', fontSize: 13, borderBottom: '1px solid #eff3f4', paddingBottom: 10 }}>
        {isTruth ? 'Truth Social' : '· X'}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 320, marginTop: 8 }}>
        <Icon d="M1.751 10c0-4.42 3.584-8 8.005-8h4.366c4.49 0 8.129 3.64 8.129 8.13 0 2.96-1.607 5.68-4.196 7.11l-8.054 4.46v-3.69h-.067c-4.49.1-8.183-3.51-8.183-8.01z" />
        <Icon d="M4.5 3.88l4.432 4.14-1.364 1.46L5.5 7.6v6.91c0 1.1.896 2 2 2h6v2h-6c-2.21 0-4-1.79-4-4V7.6L1.432 9.48.068 8.02 4.5 3.88zm15 6.51v6.91c0 1.1-.896 2-2 2h-6v-2h6V7.6l-2.068 1.88-1.364-1.46L18.5 3.88l4.432 4.14-1.364 1.46L19.5 7.6z" />
        <Icon d="M16.697 5.5c-1.222-.06-2.679.51-3.89 2.16l-.805 1.09-.806-1.09C9.984 6.01 8.526 5.44 7.304 5.5c-1.243.07-2.349.78-2.91 1.91-.552 1.12-.633 2.78.479 4.82 1.074 1.97 3.257 4.27 7.129 6.61 3.87-2.34 6.052-4.64 7.126-6.61 1.111-2.04 1.03-3.7.477-4.82-.561-1.13-1.666-1.84-2.909-1.91z" />
        <Icon d="M12 2.59l5.7 5.7-1.41 1.42L13 6.41V16h-2V6.41l-3.3 3.3-1.41-1.42L12 2.59zM21 15l-.02 3.51c0 1.38-1.12 2.49-2.5 2.49H5.5C4.11 21 3 19.88 3 18.5V15h2v3.5c0 .28.22.5.5.5h12.98c.28 0 .5-.22.5-.5L19 15h2z" />
      </div>
    </div>
  );
};

// ── Outlet logo (brand-faithful mastheads) ───────────────────────────────
// Real wordmarks/marks per outlet, modeled on the original saved pages. Iconic
// outlets get a recreated mark; the rest get a typographically-correct wordmark.
// Rendered on the masthead's brand-colored background (style.headerBg).
const Logo: React.FC<{ source: string; style: OutletStyle }> = ({ source, style }) => {
  const t = style.headerText;
  // If an official logo image has been supplied, use it verbatim.
  if (style.logoSrc) {
    // Prefix CRA's PUBLIC_URL so the asset resolves whether the app is served at
    // the web root or under a sub-path (production is hosted under /ftt/). In the
    // node-rendered preview PUBLIC_URL is empty, leaving '/logos/..' which the
    // preview generator rewrites to a relative path for file:// viewing.
    const base = process.env.PUBLIC_URL || '';
    return <img src={`${base}${style.logoSrc}`} alt={source} style={{ height: 34, maxWidth: 320, objectFit: 'contain', display: 'block' }} />;
  }
  switch (source) {
    case 'CNN':
      // White CNN inside the red masthead — the actual CNN mark.
      return <span style={{ fontFamily: SANS, fontWeight: 900, fontSize: '1.7rem', letterSpacing: '-0.04em', color: '#fff' }}>CNN</span>;
    case 'Associated Press':
      // Red "AP" + grey "NEWS".
      return (
        <span style={{ fontFamily: SANS, display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontWeight: 900, fontSize: '1.7rem', color: '#e31c23', letterSpacing: '-0.02em' }}>AP</span>
          <span style={{ fontWeight: 800, fontSize: '1.05rem', color: '#222', letterSpacing: '0.02em' }}>NEWS</span>
        </span>
      );
    case 'Reuters':
      // Orange dot-grid mark + lowercase serif wordmark.
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden>
            {[0, 7, 14].map(x => [0, 7, 14].map(y => <circle key={`${x}-${y}`} cx={x + 3} cy={y + 3} r="2.4" fill="#ff8000" />))}
          </svg>
          <span style={{ fontFamily: SERIF, fontWeight: 400, fontSize: '1.5rem', color: '#000' }}>Reuters</span>
        </span>
      );
    case 'Vox':
      return <span style={{ fontFamily: SANS, fontWeight: 900, fontSize: '1.7rem', color: '#000', letterSpacing: '-0.03em' }}>Vox</span>;
    case 'Fox News':
    case 'Fox Business':
      // Heavy "FOX" + lighter sub-brand, white on the brand background.
      return (
        <span style={{ fontFamily: SANS, display: 'inline-flex', alignItems: 'baseline', gap: 6, color: t }}>
          <span style={{ fontWeight: 900, fontSize: '1.7rem', fontStyle: 'italic', letterSpacing: '-0.03em' }}>FOX</span>
          <span style={{ fontWeight: 700, fontSize: '1.05rem', letterSpacing: '0.06em' }}>{source === 'Fox Business' ? 'BUSINESS' : 'NEWS'}</span>
        </span>
      );
    case 'Breitbart':
      // Orange square "B" mark + wordmark.
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: t }}>
          <span style={{ background: style.accentColor, color: '#fff', fontWeight: 900, fontFamily: SANS, fontSize: '1.2rem', width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 3 }}>B</span>
          <span style={{ fontFamily: SANS, fontWeight: 800, fontSize: '1.35rem', letterSpacing: '0.02em' }}>BREITBART</span>
        </span>
      );
    case 'The Wall Street Journal':
      // Broadsheet wordmark bracketed by thin rules.
      return (
        <span style={{ display: 'inline-block', borderTop: '1px solid #000', borderBottom: '1px solid #000', padding: '3px 0' }}>
          <span style={{ fontFamily: SERIF, fontWeight: 700, fontSize: '1.15rem', letterSpacing: '0.01em', color: '#000' }}>THE WALL STREET JOURNAL.</span>
        </span>
      );
    case 'MSNBC':
      // Lowercase wordmark with the brand's gold accent dot.
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: t }}>
          <span style={{ fontFamily: SANS, fontWeight: 800, fontSize: '1.6rem', letterSpacing: '-0.02em' }}>msnbc</span>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: style.accentColor, display: 'inline-block' }} />
        </span>
      );
    case 'New York Post':
      // Condensed serif masthead.
      return <span style={{ fontFamily: SERIF, fontWeight: 800, fontSize: '1.65rem', fontStyle: 'italic', letterSpacing: '-0.01em', color: t }}>New York Post</span>;
    case 'The Guardian':
      // Soft serif masthead, lining up with the brand's blue header.
      return <span style={{ fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '1.6rem', letterSpacing: '-0.01em', color: t }}>The Guardian</span>;
    case 'PBS NewsHour':
      // Boxed "PBS" + "NewsHour", echoing the broadcast lockup.
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: t }}>
          <span style={{ background: '#fff', color: style.headerBg, fontFamily: SANS, fontWeight: 800, fontSize: '1rem', padding: '3px 7px', borderRadius: 2, letterSpacing: '0.04em' }}>PBS</span>
          <span style={{ fontFamily: SANS, fontWeight: 700, fontSize: '1.35rem', letterSpacing: '0.01em' }}>NewsHour</span>
        </span>
      );
    default: {
      // Typographically-correct wordmark + thin brand-accent rule for the rest.
      const onLight = style.headerBg === '#fff' || style.headerBg === '#fff200';
      return (
        <span style={{ display: 'inline-block', borderBottom: `3px solid ${onLight ? style.accentColor : 'rgba(255,255,255,0.55)'}`, paddingBottom: 3 }}>
          <span style={{
            fontFamily: style.logoFont, fontSize: style.logoText.length > 16 ? '1.3rem' : '1.6rem',
            fontWeight: style.logoBold ? 800 : 600, letterSpacing: style.logoBold ? '0.03em' : '0.005em', color: t,
          }}>{style.logoText}</span>
        </span>
      );
    }
  }
};

// ── Share-affordance bar (icons only — NO counts, so no social-proof cue) ──
// Models the share row every original outlet page carried, in the outlet accent.
const ShareBar: React.FC<{ accent: string }> = ({ accent }) => {
  const Btn = ({ d, label }: { d: string; label: string }) => (
    <span
      aria-label={label}
      style={{
        width: 34, height: 34, borderRadius: '50%', border: `1px solid ${accent}`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <svg viewBox="0 0 24 24" width="16" height="16" fill={accent} aria-hidden><path d={d} /></svg>
    </span>
  );
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '4px 0 18px' }}>
      <span style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#888', marginRight: 4 }}>Share</span>
      {/* Facebook */}
      <Btn label="Share on Facebook" d="M22 12a10 10 0 1 0-11.56 9.88v-6.99H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.44 2.89h-2.34v6.99A10 10 0 0 0 22 12z" />
      {/* X */}
      <Btn label="Share on X" d="M18.9 1.9h3.3l-7.2 8.2 8.5 11.3h-6.7l-5.2-6.9-6 6.9H1.5l7.7-8.8L1 1.9h6.8l4.7 6.3 6.4-6.3zm-1.2 18h1.8L7.1 3.8H5.2L17.7 19.9z" />
      {/* Email */}
      <Btn label="Share by email" d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zm8 7L4.5 6.5h15L12 11zm0 2.2L4 8.2V18h16V8.2l-8 5z" />
      {/* Copy link */}
      <Btn label="Copy link" d="M3.9 12a3.1 3.1 0 0 1 3.1-3.1h4V7H7a5 5 0 0 0 0 10h4v-1.9H7A3.1 3.1 0 0 1 3.9 12zM13 7v1.9h4A3.1 3.1 0 0 1 17 15h-4V17h4a5 5 0 0 0 0-10h-4zm-5 6h8v-2H8v2z" />
    </div>
  );
};

const NewsArticleRenderer: React.FC<Props> = ({ article, onMouseEnter, onMouseLeave, onRegionHover }) => {
  const style = sourceStyles[article.source] || sourceStyles['Associated Press'];

  // Props for a hoverable/selectable region: a data-region marker (used by the
  // text-selection listener to know what was selected) plus enter/leave dwell hooks.
  const reg = (region: string) => ({
    'data-region': region,
    onMouseEnter: onRegionHover ? () => onRegionHover(region, true) : undefined,
    onMouseLeave: onRegionHover ? () => onRegionHover(region, false) : undefined,
  });

  return (
    <div
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        maxWidth: '780px', margin: '0 auto', backgroundColor: '#fff',
        border: '1px solid #e0e0e0', borderRadius: '2px', overflow: 'hidden', fontFamily: style.bodyFont,
      }}
    >
      {/* Masthead */}
      <div {...reg('source-masthead')} style={{
        backgroundColor: style.headerBg, color: style.headerText, padding: '12px 20px',
        borderBottom: style.headerBg === '#fff' || style.headerBg === '#fff200' ? '2px solid ' + style.accentColor : 'none',
        display: 'flex', alignItems: 'center',
      }}>
        <Logo source={article.source} style={style} />
      </div>

      {/* Nav */}
      <div style={{
        display: 'flex', gap: 0, padding: '0 20px', borderBottom: '1px solid #e0e0e0',
        backgroundColor: '#f8f8f8', fontSize: '0.8rem', overflow: 'hidden',
      }}>
        {style.navItems.map((item, i) => (
          <span key={i} style={{ padding: '8px 12px', color: '#333', borderRight: '1px solid #e8e8e8', whiteSpace: 'nowrap' }}>{item}</span>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '24px 20px 32px' }}>
        {/* Section kicker (as real outlet pages tag a story) */}
        <div style={{
          color: style.accentColor, fontFamily: SANS, fontSize: '0.72rem', fontWeight: 800,
          textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10,
        }}>{sectionFor(article)}</div>

        <h1 {...reg('headline')} style={{ fontFamily: style.headlineFont, fontSize: '1.75rem', lineHeight: 1.25, fontWeight: 700, margin: '0 0 14px', color: '#111' }}>
          {article.headline}
        </h1>

        {/* Byline row: avatar + author with outlet attribution + reading time */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%', background: style.accentColor, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
            fontSize: 14, fontFamily: SANS, flexShrink: 0,
          }}>{article.author.replace(/,.*/, '').split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase()}</div>
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ color: style.bylineColor, fontSize: '0.85rem' }}>
              By <span style={{ fontWeight: 600 }}>{article.author.replace(new RegExp(`,?\\s*${style.logoText}$`, 'i'), '')}</span>
              <span style={{ color: '#aaa' }}> · {article.source}</span>
            </div>
            <div style={{ color: '#999', fontSize: '0.78rem' }}>
              {article.publicationDate}<span style={{ margin: '0 6px' }}>·</span>{readingMinutes(article.body)} min read
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 18, paddingBottom: 16, borderBottom: '1px solid #e8e8e8' }}>
          <ShareBar accent={style.accentColor} />
          {article.sourceUrl && <div style={{ color: '#bbb', fontSize: '0.72rem' }}>{article.sourceUrl}</div>}
        </div>

        {/* Lead paragraph */}
        {(() => {
          const paras = article.body.split('\n\n');
          return (
            <>
              {paras.slice(0, 1).map((para, idx) => (
                <p key={`lead-${idx}`} {...reg('body-p0')} style={{ fontSize: '1.05rem', lineHeight: 1.8, color: '#222', marginBottom: '1rem', fontFamily: BODY }}>{para.trim()}</p>
              ))}
              {/* Endorser embedded as a tweet, after the lead (as in the original) */}
              {article.endorser && (
                <div {...reg('endorser')}>
                  <TweetEmbed endorser={article.endorser} />
                </div>
              )}
              {paras.slice(1).map((para, idx) => (
                <p key={`p-${idx}`} {...reg(`body-p${idx + 1}`)} style={{ fontSize: '1.05rem', lineHeight: 1.8, color: '#222', marginBottom: '1rem', fontFamily: BODY }}>{para.trim()}</p>
              ))}
            </>
          );
        })()}
      </div>

      {/* Outlet footer (every original page carried a branded footer strip) */}
      <div style={{
        backgroundColor: style.headerBg === '#fff' || style.headerBg === '#fff200' ? '#f3f3f3' : style.headerBg,
        color: style.headerBg === '#fff' || style.headerBg === '#fff200' ? '#555' : style.headerText,
        padding: '14px 20px', fontFamily: SANS, fontSize: '0.72rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8,
        opacity: 0.92,
      }}>
        <span>{['Terms of Use', 'Privacy Policy', 'Contact'].join('   ·   ')}</span>
        <span>© {new Date().getFullYear()} {article.source}. All rights reserved.</span>
      </div>
    </div>
  );
};

export default NewsArticleRenderer;
